import React, { useEffect, useState } from "react";
import HeaderLogo from "../../src/assets/media/logo.png";
import LoginIllus from "../../src/assets/media/login_illustration.jpg";
import { IoMdMail, IoMdLock } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { jwtDecode } from "jwt-decode";
import LoginAPI from "../api/LoginAPI";
import RegisterAPI from "../api/RegisterAPI";
import ForgetEmailAPI from "../api/ForgetEmailAPI";
import toast from "react-hot-toast";
import { IoEye, IoEyeOff } from "react-icons/io5";
import VerifyOtpAPI from "../api/VerifyOtpAPI";
import ChangePasswordAPI from "../api/ChangePassword";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa6";

const Login = ({ loginToggle, setLoginToggle, onLoginSuccess }) => {
  const navigate = useNavigate();

  // State management
  const [view, setView] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Login states
  const [loginDetails, setLoginDetails] = useState({
    email: "",
    password: "",
  });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginTouched, setLoginTouched] = useState({});

  // Register states
  const [registerDetails, setRegisterDetails] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [registerErrors, setRegisterErrors] = useState({});
  const [registerTouched, setRegisterTouched] = useState({});

  // Forgot password states
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotTouched, setForgotTouched] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpTouched, setOtpTouched] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: "",
    confirm_password: "",
  });
  const [resetErrors, setResetErrors] = useState({});
  const [resetTouched, setResetTouched] = useState({});
  const [storedEmailForOtp, setStoredEmailForOtp] = useState("");

  // Helper functions
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const clearAllStates = () => {
    setLoginDetails({ email: "", password: "" });
    setRegisterDetails({ name: "", email: "", password: "", confirm_password: "" });
    setForgotEmail("");
    setOtp("");
    setNewPassword({ password: "", confirm_password: "" });
    setLoginErrors({});
    setRegisterErrors({});
    setForgotError("");
    setOtpError("");
    setResetErrors({});
    setLoginTouched({});
    setRegisterTouched({});
    setForgotTouched(false);
    setOtpTouched(false);
    setResetTouched({});
    setShowPassword(false);
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    clearAllStates();
    setView("login");
    setLoginToggle(false);
  };

  // Login handlers
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginDetails(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (loginErrors[name]) {
      setLoginErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleLoginBlur = (e) => {
    const { name } = e.target;
    setLoginTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched when form is submitted
    setLoginTouched({
      email: true,
      password: true,
    });

    const { email, password } = loginDetails;
    const errors = {};

    // Validation
    if (!email.trim()) errors.email = "Email is required.";
    else if (!isValidEmail(email)) errors.email = "Enter a valid email.";

    if (!password.trim()) errors.password = "Password is required.";
    else if (password.length < 6) errors.password = "Password must be at least 6 characters.";

    setLoginErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);

    try {
      const response = await LoginAPI.postLoginDetails(loginDetails);

      if (response.code === 200) {
        toast.success("Login successful!");

        // Store auth data properly
        const authData = {
          token: response.data.token,
          user: {
            id: response.data.user_id || response.data.id,
            name: response.data.name,
            email: response.data.email,
            avatar: response.data.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(response.data.name || 'User')}&background=random`
          }
        };

        localStorage.setItem("authToken", authData.token);
        localStorage.setItem("user", JSON.stringify(authData.user));

        // Call success handler and close modal
        onLoginSuccess(authData.user);
        handleCloseModal();

        // Navigate to profile
        navigate(`/profile/${authData.user.id}`);
      } else {
        toast.error(response.message || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Register handlers
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterDetails(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (registerErrors[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleRegisterBlur = (e) => {
    const { name } = e.target;
    setRegisterTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched when form is submitted
    setRegisterTouched({
      name: true,
      email: true,
      password: true,
      confirm_password: true,
    });

    const { name, email, password, confirm_password } = registerDetails;
    const errors = {};

    // Validation
    if (!name.trim()) errors.name = "Name is required.";
    if (!email.trim()) errors.email = "Email is required.";
    else if (!isValidEmail(email)) errors.email = "Enter a valid email.";
    if (!password.trim()) errors.password = "Password is required.";
    else if (password.length < 6) errors.password = "Password must be at least 6 characters.";
    if (!confirm_password.trim()) errors.confirm_password = "Confirm your password.";
    else if (password !== confirm_password) errors.confirm_password = "Passwords do not match.";

    setRegisterErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);

    try {
      const response = await RegisterAPI.postRegisterDetails(registerDetails);

      if (response.code === 200) {
        toast.success("Registration successful! Please login.");
        clearAllStates();
        setView("login");
      } else {
        toast.error(response.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password handlers
  const handleForgotSubmit = async (e) => {
    e.preventDefault();

    setForgotTouched(true);

    const emailInput = forgotEmail.trim();
    if (!emailInput) {
      setForgotError("Email is required.");
      return;
    }

    if (!isValidEmail(emailInput)) {
      setForgotError("Enter a valid email.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await ForgetEmailAPI.postLoginDetails({ email: emailInput });

      if (response.code === 200) {
        toast.success("OTP sent to your email.");
        setStoredEmailForOtp(forgotEmail);
        setForgotEmail("");
        setForgotError("");
        setForgotTouched(false);
        setView("verifyOtp");
      } else {
        setForgotError("Email not found or something went wrong.");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setForgotError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // OTP verification
  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    setOtpTouched(true);

    if (!otp.trim()) {
      setOtpError("OTP is required.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await VerifyOtpAPI.postOtpVerify({
        email: storedEmailForOtp,
        otp,
      });

      if (response.code === 200) {
        toast.success("OTP verified!");
        setOtpError("");
        setOtpTouched(false);
        setView("resetPassword");
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setOtpError("Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched when form is submitted
    setResetTouched({
      password: true,
      confirm_password: true,
    });

    const { password, confirm_password } = newPassword;
    const errors = {};

    if (!password) errors.password = "Password is required.";
    else if (password.length < 6) errors.password = "Password must be at least 6 characters.";
    if (!confirm_password) errors.confirm_password = "Please confirm your password.";
    else if (password !== confirm_password) errors.confirm_password = "Passwords do not match.";

    setResetErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);

    try {
      const response = await ChangePasswordAPI.postChangePassword({
        email: storedEmailForOtp,
        new_password: password,
        confirm_password: confirm_password,
      });

      if (response.code === 200) {
        toast.success("Password reset successful! Please login.");
        clearAllStates();
        setView("login");
      } else if (response.code === 400 && response.message && response.message.toLowerCase().includes('same') ||
        response.message && response.message.toLowerCase().includes('old') ||
        response.message && response.message.toLowerCase().includes('current')) {
        // Handle same password error from server
        setResetErrors(prev => ({ ...prev, password: "You're entering your old password. Please try a new password." }));
        toast.error("Please choose a different password from your current one.");
      } else {
        toast.error(response.message || "Failed to reset password. Please try again.");
      }
    } catch (error) {
      console.error("Reset password error:", error);

      // Check if error response indicates same password
      if (error.response && error.response.data && error.response.data.message) {
        const errorMessage = error.response.data.message.toLowerCase();
        if (errorMessage.includes('same') || errorMessage.includes('old') || errorMessage.includes('current')) {
          setResetErrors(prev => ({ ...prev, password: "You're entering your old password. Please try a new password." }));
          toast.error("Please choose a different password from your current one.");
          return;
        }
      }

      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Google login handler
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);

    try {
      const decoded = jwtDecode(credentialResponse.credential);

      const response = await fetch("http://localhost:8000/api/loginwithgmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: decoded.name || "Google User",
          email: decoded.email,
        }),
      });

      const data = await response.json();

      if (data.code === 200) {
        toast.success("Google login successful!");

        // Store auth data properly
        const authData = {
          token: data.token,
          user: {
            id: data.user.id,
            name: data.user.name,
            email: data.user.email,
            avatar: decoded.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.user.name)}&background=random`
          }
        };

        localStorage.setItem("authToken", authData.token);
        localStorage.setItem("user", JSON.stringify(authData.user));

        onLoginSuccess(authData.user);
        handleCloseModal();
      } else {
        toast.error(data.message || "Google authentication failed");
      }
    } catch (error) {
      console.error("Google login failed:", error);
      toast.error("Google login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll lock effect
  useEffect(() => {
    if (loginToggle) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [loginToggle]);

  // Reset states when modal closes
  useEffect(() => {
    if (!loginToggle) {
      clearAllStates();
      setView("login");
    }
  }, [loginToggle]);

  const renderForm = () => {
    switch (view) {
      case "login":
        return (
          <>
            <h3 className="text-center py-3">Login</h3>
            <div className="custom-sorted-trips-form-container">
              <form onSubmit={handleLoginSubmit}>
                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><IoMdMail /></span>
                    <input
                      type="email"
                      name="email"
                      value={loginDetails.email}
                      onChange={handleLoginChange}
                      onBlur={handleLoginBlur}
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>
                  {loginTouched.email && loginErrors.email && (
                    <div className="error-message">{loginErrors.email}</div>
                  )}
                </div>

                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs position-relative">
                    <span><IoMdLock /></span>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={loginDetails.password}
                      onChange={handleLoginChange}
                      onBlur={handleLoginBlur}
                      placeholder="Enter your password"
                      disabled={isLoading}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "0",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        zIndex: 2,
                        border: "none",
                      }}
                    >
                      {showPassword ? <IoEyeOff /> : <IoEye />}
                    </span>
                  </div>
                  {loginTouched.password && loginErrors.password && (
                    <div className="error-message">{loginErrors.password}</div>
                  )}
                </div>

                <div className="custom-sorted-trips-form-forget-password">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setView("forgot");
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>
                <div className="custom-sorted-trips-form-login-btn-container">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Logging in..." : "Login"}
                  </button>
                </div>
              </form>
            </div>
            <h6 className="pt-2 pb-1 mb-0">OR</h6>
            <div className="pt-0">
              New User?{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setView("register");
                }}
              >
                Register
              </a>
            </div>
          </>
        );

      case "register":
        return (
          <>
            <h3 className="text-center py-3 mb-0">Register</h3>
            <div className="custom-sorted-trips-form-container">
              <form onSubmit={handleRegisterSubmit}>
                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><FaUser /></span>
                    <input
                      type="text"
                      name="name"
                      value={registerDetails.name}
                      onChange={handleRegisterChange}
                      onBlur={handleRegisterBlur}
                      placeholder="Enter your name"
                      disabled={isLoading}
                    />
                  </div>
                  {registerTouched.name && registerErrors.name && (
                    <div className="error-message">{registerErrors.name}</div>
                  )}
                </div>

                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><IoMdMail /></span>
                    <input
                      type="email"
                      name="email"
                      value={registerDetails.email}
                      onChange={handleRegisterChange}
                      onBlur={handleRegisterBlur}
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>
                  {registerTouched.email && registerErrors.email && (
                    <div className="error-message">{registerErrors.email}</div>
                  )}
                </div>

                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><IoMdLock /></span>
                    <input
                      type="password"
                      name="password"
                      value={registerDetails.password}
                      onChange={handleRegisterChange}
                      onBlur={handleRegisterBlur}
                      placeholder="Enter your password"
                      disabled={isLoading}
                    />
                  </div>
                  {registerTouched.password && registerErrors.password && (
                    <div className="error-message">{registerErrors.password}</div>
                  )}
                </div>

                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><IoMdLock /></span>
                    <input
                      type="password"
                      name="confirm_password"
                      value={registerDetails.confirm_password}
                      onChange={handleRegisterChange}
                      onBlur={handleRegisterBlur}
                      placeholder="Confirm password"
                      disabled={isLoading}
                    />
                  </div>
                  {registerTouched.confirm_password && registerErrors.confirm_password && (
                    <div className="error-message">{registerErrors.confirm_password}</div>
                  )}
                </div>

                <div className="custom-sorted-trips-form-login-btn-container">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Registering..." : "Register"}
                  </button>
                </div>
              </form>
            </div>
            <h6 className="pt-2 pb-1 mb-0">OR</h6>
            <div className="pt-0">
              Already have an account?{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setView("login");
                }}
              >
                Login
              </a>
            </div>
          </>
        );

      case "forgot":
        return (
          <>
            <h3 className="text-center py-3">Reset Password</h3>
            <div className="custom-sorted-trips-form-container">
              <form onSubmit={handleForgotSubmit}>
                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <span><IoMdMail /></span>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => {
                        setForgotEmail(e.target.value);
                        if (forgotError) {
                          setForgotError("");
                        }
                      }}
                      placeholder="Enter your registered email"
                      disabled={isLoading}
                    />
                  </div>
                  {forgotTouched && forgotError && (
                    <div className="error-message">{forgotError}</div>
                  )}
                </div>

                <div className="custom-sorted-trips-form-login-btn-container">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Sending..." : "Send OTP"}
                  </button>
                </div>
              </form>
            </div>
            <div className="pt-2">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setView("login");
                }}
              >
                Back to Login
              </a>
            </div>
          </>
        );

      case "verifyOtp":
        return (
          <>
            <h3 className="text-center py-3">Verify OTP</h3>
            <div className="custom-sorted-trips-form-container">
              <form onSubmit={handleOtpSubmit}>
                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (otpError) {
                          setOtpError("");
                        }
                      }}
                      placeholder="Enter the OTP"
                      disabled={isLoading}
                    />
                  </div>
                  {otpTouched && otpError && (
                    <div className="error-message">{otpError}</div>
                  )}
                </div>

                <div className="custom-sorted-trips-form-login-btn-container">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </form>
              <div className="pt-2">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setView("forgot");
                  }}
                >
                  Back
                </a>
              </div>
            </div>
          </>
        );

      case "resetPassword":
        return (
          <>
            <h3 className="text-center py-3">Set New Password</h3>
            <div className="custom-sorted-trips-form-container">
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <input
                      type="password"
                      value={newPassword.password}
                      onChange={(e) => {
                        setNewPassword(prev => ({ ...prev, password: e.target.value }));
                        if (resetErrors.password) {
                          setResetErrors(prev => ({ ...prev, password: "" }));
                        }
                      }}
                      placeholder="New Password"
                      disabled={isLoading}
                    />
                  </div>
                  {resetTouched.password && resetErrors.password && (
                    <div className="error-message">{resetErrors.password}</div>
                  )}
                </div>

                <div className="custom-input-group">
                  <div className="custom-sorted-trips-form-inputs">
                    <input
                      type="password"
                      value={newPassword.confirm_password}
                      onChange={(e) => {
                        setNewPassword(prev => ({ ...prev, confirm_password: e.target.value }));
                        if (resetErrors.confirm_password) {
                          setResetErrors(prev => ({ ...prev, confirm_password: "" }));
                        }
                      }}
                      placeholder="Confirm New Password"
                      disabled={isLoading}
                    />
                  </div>
                  {resetTouched.confirm_password && resetErrors.confirm_password && (
                    <div className="error-message">{resetErrors.confirm_password}</div>
                  )}
                </div>

                <div className="custom-sorted-trips-form-login-btn-container">
                  <button type="submit" disabled={isLoading}>
                    {isLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <section className="custom-sorted-trips-login-section">
      <div className="custom-sorted-trips-login-container">
        <div className="custom-sorted-trips-login-cross-icon">
          <RxCross2
            className="fs-3"
            onClick={handleCloseModal}
            style={{ cursor: "pointer" }}
          />
        </div>
        <div className="container-fluid position-relative" style={{ backgroundColor: "white" }}>
          <div className="row">
            <div className="col-12 col-md-6 col-lg-6 px-0 d-none d-md-block">
              <div className="custom-sorted-trips-login-img-banner">
                <img src={LoginIllus} alt="login illustration" />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-6 pt-4 pb-3 pb-md-0 pb-lg-0 d-lg-flex flex-lg-column justify-content-lg-center align-items-lg-center">
              <div className="custom-sorted-trips-right-box">
                <div className="custom-sorted-trips-login-logo-img">
                  <img src={HeaderLogo} alt="logo" />
                </div>
                {renderForm()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;